// 発注検索(発注処理・発注履歴)の検索条件モデル(純関数)。
//
// 条件オブジェクトの形:
//   {
//     dateKinds: ["placed"],        // 日付種類コード。常に1つ以上(「すべて」=全種列挙)
//     from: Date, to: Date,          // 期間(日単位に丸め済み・常に from <= to)
//     statuses: ["placed"],          // 状態コード。常に1つ以上
//     departmentIds: null | ["101"], // null=「すべて」。部署マスタは増減するため
//                                    //   全列挙ではなく null で表す(全列挙だとマスタに部署を
//                                    //   追加した瞬間、保存済みの「すべて」が全部でなくなる)
//     myOwn: false,                  // このアカウントのみ
//     orderNumber: "",               // 発注番号(履歴画面のみ。URL専用でLSには保存しない)
//   }
//
// コードは URL パラメータ用の ASCII 値。表示名・Firestore フィールドとの対応は
// 既存定数(ORDER_DATE_FIELDS / orderStatus.js)から導出し、対応表の二重管理を避ける。
import {
  ORDER_PLACED,
  ORDER_CONFIRMED,
  ORDER_RECEIVED,
  ORDER_CANCELLED,
} from "../../constants/orderStatus";
import { PERMISSIONS } from "../../constants/permissions";
import { ORDER_DATE_FIELDS } from "./orderDateUpdate";

// --- 状態 ---
// コードは定数名に合わせる(ORDER_CONFIRMED=発注済 → "confirmed")。
// 色は一覧(TableArea.module.scss)のステータス色と同じ値。
export const STATUS_OPTIONS = [
  { id: "placed", label: ORDER_PLACED, color: "#3b96f1", textColor: "#fff" },
  { id: "confirmed", label: ORDER_CONFIRMED, color: "#f44336", textColor: "#fff" },
  { id: "received", label: ORDER_RECEIVED, color: "#8559eb", textColor: "#fff" },
  { id: "canceled", label: ORDER_CANCELLED, color: "#e0e0e0", textColor: "#000" },
];
export const STATUS_IDS = STATUS_OPTIONS.map((option) => option.id);

const STATUS_LABEL_BY_ID = STATUS_OPTIONS.reduce(
  (acc, { id, label }) => ({ ...acc, [id]: label }),
  {}
);
// コード配列 → Firestore に入っている表示名(注文済 等)の配列
export const statusLabelsForIds = (ids) =>
  (ids || []).map((id) => STATUS_LABEL_BY_ID[id]).filter(Boolean);

const STATUS_OPTION_BY_LABEL = STATUS_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.label]: option }),
  {}
);

// --- 日付種類 ---
// コードはフィールド名から導出(placed_at → "placed")。並びは業務の進行順。
// チップの色は対応する状態と同じにする(発注日=発注済の赤 等)。日付と状態の
// 対応は ORDER_DATE_FIELDS の status で定義済みのため、そこから引いて二重管理を避ける。
export const DATE_KIND_OPTIONS = ORDER_DATE_FIELDS.map(
  ({ field, label, status }) => ({
    id: field.replace(/_at$/, ""),
    field,
    label,
    color: STATUS_OPTION_BY_LABEL[status]?.color,
    textColor: STATUS_OPTION_BY_LABEL[status]?.textColor,
  })
);
export const DATE_KIND_IDS = DATE_KIND_OPTIONS.map((option) => option.id);

const DATE_KIND_FIELD_BY_ID = DATE_KIND_OPTIONS.reduce(
  (acc, { id, field }) => ({ ...acc, [id]: field }),
  {}
);
export const dateKindToField = (id) => DATE_KIND_FIELD_BY_ID[id];

// --- 画面種別 ---
export const SEARCH_SCREENS = {
  PROCESSING: "processing",
  HISTORY: "history",
};

// --- 既定値 ---
// 過去3ヶ月(現行踏襲): 2ヶ月前の月初〜今月末
export const getDefaultRange = (now = new Date()) => ({
  from: new Date(now.getFullYear(), now.getMonth() - 2, 1),
  to: new Date(now.getFullYear(), now.getMonth() + 1, 0),
});

const toValidDate = (value) =>
  value instanceof Date && !Number.isNaN(value.getTime()) ? value : null;

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

// 条件の正規化 + 権限クランプ。
//  ・不正なコードは除去し、空になった複数選択は「すべて(全選択)」に戻す
//    (UI 側の「空選択という曖昧な状態を作らない」仕様と対で使う)
//  ・from > to は入れ替え、時刻は日単位に丸める
//  ・URL/LS に権限外の値が来ても、その役割で見られる範囲に強制する
export const normalizeConditions = (userInfo, cond = {}, now = new Date()) => {
  const role = userInfo?.role;
  // マスタ・注文docと同様に文字列へ寄せる(数値のまま持つと部署選択UIの id 比較で外れる)
  const ownDepartmentId = userInfo?.order_department_id
    ? String(userInfo.order_department_id)
    : "";

  // 日付種類: 業務順に並べ直す(URL 正規形を一意にするため)
  const dateKindSet = new Set(Array.isArray(cond.dateKinds) ? cond.dateKinds : []);
  let dateKinds = DATE_KIND_IDS.filter((id) => dateKindSet.has(id));
  if (dateKinds.length === 0) {
    dateKinds = [...DATE_KIND_IDS];
  }

  const statusSet = new Set(Array.isArray(cond.statuses) ? cond.statuses : []);
  let statuses = STATUS_IDS.filter((id) => statusSet.has(id));
  if (statuses.length === 0) {
    statuses = [...STATUS_IDS];
  }

  const defaultRange = getDefaultRange(now);
  let from = startOfDay(toValidDate(cond.from) || defaultRange.from);
  let to = startOfDay(toValidDate(cond.to) || defaultRange.to);
  if (from > to) {
    [from, to] = [to, from];
  }

  // 部署: 空文字・重複を除去。空配列は「すべて」(null)。
  // 並びは数値視の昇順に統一(URL 正規形を一意にするため)
  let departmentIds = null;
  if (Array.isArray(cond.departmentIds)) {
    const cleaned = [
      ...new Set(
        cond.departmentIds
          .map((value) => (value == null ? "" : String(value)))
          .filter((value) => value !== "")
      ),
    ];
    // numeric な localeCompare は "01" と "1" を同値と判定するため、
    // 第2キーに素の比較を足して並びを完全に一意にする(URL 正規形の一意性のため)
    cleaned.sort(
      (a, b) =>
        a.localeCompare(b, "ja", { numeric: true }) ||
        (a < b ? -1 : a > b ? 1 : 0)
    );
    departmentIds = cleaned.length > 0 ? cleaned : null;
  }

  let myOwn = cond.myOwn === true;

  const orderNumber =
    typeof cond.orderNumber === "string" && /^\d+$/.test(cond.orderNumber)
      ? cond.orderNumber
      : "";

  // 権限クランプ(現行の disabled 実装と同じ制約をデータ側でも保証する)
  if (role === PERMISSIONS.ORDER_USER) {
    // 発注担当者は自部署のみ。自部署が未設定なら「すべて」に落とす
    // (空文字での絞り込みは常に0件になり、不具合と区別がつかないため)
    departmentIds = ownDepartmentId ? [ownDepartmentId] : null;
  } else if (role === PERMISSIONS.NORMAL_USER) {
    // 一般ユーザーは全部署 + 自分の注文のみ
    departmentIds = null;
    myOwn = true;
  } else if (role === PERMISSIONS.VIEWER) {
    departmentIds = ownDepartmentId ? [ownDepartmentId] : null;
    myOwn = false;
  }
  // 管理者・人事担当者は制限なし

  return { dateKinds, from, to, statuses, departmentIds, myOwn, orderNumber };
};

// ロール別の既定値。
// 履歴画面の状態だけ「すべて」にする(注文が発注済へ進むと既定の「注文済のみ」では
// 見えなくなり「注文が消えた」と誤解されるため)。処理画面は業務動線どおり注文済のみ。
export const getDefaultConditions = (screen, userInfo, now = new Date()) => {
  const { from, to } = getDefaultRange(now);
  return normalizeConditions(
    userInfo,
    {
      dateKinds: ["placed"],
      from,
      to,
      statuses:
        screen === SEARCH_SCREENS.HISTORY ? [...STATUS_IDS] : ["placed"],
      departmentIds: userInfo?.order_department_id
        ? [userInfo.order_department_id]
        : null,
      myOwn: userInfo?.role === PERMISSIONS.NORMAL_USER,
      orderNumber: "",
    },
    now
  );
};

// 画面ごとの操作可否(現行踏襲)。true = 操作不可(表示はする)。
// ロックされた項目の値は normalizeConditions 側でも強制されるため、
// UI の disabled はあくまで見た目上の案内で、データはここに依存しない。
export const getFieldLocks = (screen, role) => ({
  department:
    role !== PERMISSIONS.ADMINISTRATOR &&
    role !== PERMISSIONS.HUMAN_RESOURCE_USER,
  myOwn:
    screen === SEARCH_SCREENS.HISTORY &&
    (role === PERMISSIONS.NORMAL_USER || role === PERMISSIONS.VIEWER),
});
